import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useWallet, useWalletBalance } from "@/context/WalletContext";
import { api } from "@/lib/api";
import { fmtNum, fmtSui, fmtPrice } from "@/lib/format";

const PRESETS = [0.5, 1, 5, 10];

export default function TradePanel({ coin, holding, onTraded }) {
  const { connected, address, refresh } = useWallet();
  const { balance } = useWalletBalance();
  const [side, setSide] = useState("buy");
  const [amount, setAmount] = useState("");
  const [slippage, setSlippage] = useState(3);
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { setQuote(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/coins/${coin.id}/quote`, { params: { side, amount: amt } });
        if (alive) setQuote(data);
      } catch (e) {}
    }, 200);
    return () => { alive = false; clearTimeout(t); };
  }, [amount, side, coin.id]);

  const migrated = coin.migrated;

  const doTrade = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Enter an amount");
    if (!connected) return toast.error("Connect your Sui wallet first");
    const addr = address;
    setBusy(true);
    try {
      if (side === "buy") {
        const { data } = await api.post(`/coins/${coin.id}/buy`, { address: addr, sui_amount: amt, slippage });
        toast.success(`Bought ${fmtNum(data.tokens_received)} $${coin.symbol}`);
        onTraded && onTraded(data);
      } else {
        const { data } = await api.post(`/coins/${coin.id}/sell`, { address: addr, token_amount: amt, slippage });
        toast.success(`Sold for ${fmtSui(data.sui_received, 3)} SUI`);
        onTraded && onTraded(data);
      }
      setAmount("");
      await refresh(addr);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Trade failed");
    } finally {
      setBusy(false);
    }
  };

  if (migrated) {
    return (
      <div className="card-surface p-5">
        <div className="text-center py-4">
          <div className="text-sm text-muted-foreground mb-2">This coin graduated 🎓</div>
          <a href={coin.cetus_pool?.url || "https://app.cetus.zone"} target="_blank" rel="noreferrer"
            className="inline-flex items-center justify-center w-full py-3 rounded-lg font-bold text-black" style={{ background: "linear-gradient(90deg,#00E5FF,#FF007A)" }}>
            Trade on Cetus AMM Pool
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="card-surface p-4" data-testid="trade-panel">
      <div className="grid grid-cols-2 gap-1 p-1 bg-black/30 rounded-lg mb-4">
        <button data-testid="trade-tab-buy" onClick={() => { setSide("buy"); setAmount(""); }}
          className={`py-2 rounded-md font-bold text-sm transition-all ${side === "buy" ? "text-black" : "text-muted-foreground"}`}
          style={side === "buy" ? { background: "linear-gradient(90deg,#16C784,#00E0A3)" } : {}}>
          Buy
        </button>
        <button data-testid="trade-tab-sell" onClick={() => { setSide("sell"); setAmount(""); }}
          className={`py-2 rounded-md font-bold text-sm transition-all ${side === "sell" ? "text-white" : "text-muted-foreground"}`}
          style={side === "sell" ? { background: "linear-gradient(90deg,#F43F5E,#FF007A)" } : {}}>
          Sell
        </button>
      </div>

      <div className="flex items-center justify-between mb-1.5 text-xs">
        <span className="text-muted-foreground">Amount ({side === "buy" ? "SUI" : coin.symbol})</span>
        <span className="font-mono text-muted-foreground">
          {side === "buy" ? `${fmtSui(balance)} SUI` : `${fmtNum(holding?.balance || 0)} ${coin.symbol}`}
        </span>
      </div>
      <input
        data-testid="sui-amount-input"
        type="number" step="any" min="0" value={amount}
        onChange={(e) => setAmount(e.target.value)}
        placeholder="0.00"
        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-3 text-lg font-mono font-bold focus:outline-none focus:border-[#1EC62E]/50"
      />

      <div className="grid grid-cols-4 gap-1.5 mt-2">
        {side === "buy"
          ? PRESETS.map((p) => (
              <button key={p} data-testid={`quick-sui-preset-${p}`} onClick={() => setAmount(String(p))}
                className="py-1.5 rounded-md bg-white/5 border border-white/10 text-xs font-mono hover:border-[#1EC62E]/50 transition-colors">{p}</button>
            ))
          : [25, 50, 75, 100].map((p) => (
              <button key={p} onClick={() => setAmount(String(((holding?.balance || 0) * p) / 100))}
                className="py-1.5 rounded-md bg-white/5 border border-white/10 text-xs font-mono hover:border-[#FF007A]/50 transition-colors">{p}%</button>
            ))}
      </div>

      <div className="flex items-center gap-2 mt-3 text-xs">
        <span className="text-muted-foreground">Slippage</span>
        {[1, 3, 5].map((s) => (
          <button key={s} onClick={() => setSlippage(s)}
            className={`px-2 py-1 rounded-md font-mono border ${slippage === s ? "border-[#1EC62E]/60 text-[#1EC62E]" : "border-white/10 text-muted-foreground"}`}>{s}%</button>
        ))}
      </div>

      {quote && (
        <div className="mt-4 space-y-1.5 text-xs font-mono bg-black/30 rounded-lg p-3">
          <div className="flex justify-between"><span className="text-muted-foreground">You receive</span><span className="font-bold">{side === "buy" ? `${fmtNum(quote.out)} ${coin.symbol}` : `${fmtSui(quote.out, 4)} SUI`}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Price impact</span><span className={quote.price_impact > 5 ? "text-[#F43F5E]" : "text-[#1EC62E]"}>{quote.price_impact.toFixed(2)}%</span></div>
          <div className="h-px bg-white/10 my-1" />
          <div className="flex justify-between"><span className="text-muted-foreground">Total fee (1%)</span><span>{fmtSui((quote.dev_fee || 0) + (quote.platform_fee || 0), 4)} SUI</span></div>
          <div className="flex justify-between text-[11px]"><span className="text-[#1EC62E]">↳ Dev reward 0.7%</span><span className="text-[#1EC62E]">{fmtSui(quote.dev_fee, 4)}</span></div>
          <div className="flex justify-between text-[11px]"><span className="text-[#1EC62E]">↳ Platform 0.3%</span><span className="text-[#1EC62E]">{fmtSui(quote.platform_fee, 4)}</span></div>
        </div>
      )}

      <button
        onClick={doTrade} disabled={busy}
        data-testid="submit-trade-button"
        className="w-full mt-4 py-3 rounded-lg font-display font-bold text-black transition-transform hover:scale-[1.01] disabled:opacity-50"
        style={{ background: side === "buy" ? "linear-gradient(90deg,#16C784,#00E0A3)" : "linear-gradient(90deg,#F43F5E,#FF007A)", color: side === "buy" ? "#000" : "#fff" }}>
        {busy ? "Processing..." : `${side === "buy" ? "BUY" : "SELL"} $${coin.symbol}`}
      </button>
      <p className="mt-2 text-[10px] leading-snug text-muted-foreground text-center">
        Wallet connect &amp; balances are live on Sui mainnet. Curve trades run on the demo engine until your mainnet Move package is connected.
      </p>
    </div>
  );
}
